import * as React from 'react';
import { useSwipeable, type SwipeCallback, type SwipeableHandlers, LEFT, RIGHT, UP, DOWN } from 'react-swipeable';
import { Position } from '.';

type PropType = {
  onClose: () => void;
  position: Position;
  children(handlers: SwipeableHandlers): React.ReactElement;
};

export default function Swipe({ children, position, onClose }: PropType) {
  const handlers = useSwipeable({
    onSwiping: React.useCallback<SwipeCallback>(
      ({ deltaX, deltaY, event, dir }) => {
        if ((position === Position.Left || position === Position.Right) && (dir === LEFT || dir === RIGHT)) {
          const element = event.currentTarget as HTMLDivElement;
          const width = element.offsetWidth;
          const newTranslateX = position === Position.Right ? width - deltaX : width + deltaX;

          if (newTranslateX < width) {
            element.style.transform = `translateX(${
              position === Position.Right ? `-${newTranslateX}` : newTranslateX
            }px)`;
          } else {
            element.style.transform = '';
          }
        } else if ((position === Position.Bottom || position === Position.Top) && (dir === UP || dir === DOWN)) {
          const element = event.currentTarget as HTMLDivElement;
          const height = element.offsetHeight;
          const newTranslateY = position === Position.Bottom ? height - deltaY : height + deltaY;

          if (newTranslateY < height) {
            element.style.transform = `translateY(${
              position === Position.Bottom ? `-${newTranslateY}` : newTranslateY
            }px)`;
          } else {
            element.style.transform = '';
          }
        }
      },
      [position],
    ),
    onSwiped: React.useCallback<SwipeCallback>(
      ({ dir, event }) => {
        const element = event.currentTarget as HTMLDivElement;
        if (
          (position === Position.Left && dir === LEFT) ||
          (position === Position.Right && dir === RIGHT) ||
          (position === Position.Top && dir === UP) ||
          (position === Position.Bottom && dir === DOWN)
        ) {
          onClose();
        } else {
          element.style.transform = '';
        }
      },
      [onClose, position],
    ),
  });

  return children(handlers);
}

export function useStopPropagationOnSwipe<T extends HTMLElement>() {
  const innerRef = React.useRef<T | null>(undefined);

  const callbackRef = React.useCallback((ref: T | null) => {
    innerRef.current = ref;
    if (ref) {
      ref.addEventListener('touchmove', e => e.stopPropagation());
    }
  }, []);

  React.useEffect(() => {
    return () => {
      innerRef.current?.removeEventListener('touchmove', e => e.stopPropagation());
    };
  }, []);

  return callbackRef;
}
